import { HttpClient, HttpContext, HttpContextToken } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { filter, take } from 'rxjs';
import { api } from '../../common';
import { recordRumEvent } from '../monitoring/aws-rum';

export type ClientDiagnosticLevel = 'debug' | 'info' | 'warn' | 'error';

type ClientDiagnosticEvent = {
  eventId: string;
  sequence: number;
  type: string;
  level: ClientDiagnosticLevel;
  timestamp: string;
  route: string;
  requestId?: string;
  message?: string;
  details?: Record<string, unknown>;
};

const SESSION_STORAGE_KEY = 'rep_flow_client_diagnostics_session';

export const SKIP_CLIENT_DIAGNOSTICS = new HttpContextToken<boolean>(() => false);

@Injectable({
  providedIn: 'root',
})
export class ClientDiagnosticsService {
  private readonly maxBufferedEvents = 200;
  private readonly maxBatchSize = 20;

  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly oidcSecurityService = inject(OidcSecurityService);

  private readonly sessionId = this.restoreOrCreateSessionId();

  private queue: ClientDiagnosticEvent[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private flushInFlight = false;
  private sequence = 0;
  private bootstrapped = false;
  private isAuthenticated = false;

  bootstrap(): void {
    if (this.bootstrapped) {
      return;
    }

    this.bootstrapped = true;

    this.track(
      'app.bootstrap',
      {
        href: window.location.href,
        online: navigator.onLine,
        visibilityState: document.visibilityState,
      },
      'info',
    );

    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
    window.addEventListener('pageshow', this.handlePageShow);
    window.addEventListener('pagehide', this.handlePageHide);
    window.addEventListener('error', this.handleWindowError);
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
      const navigation = event as NavigationEnd;
      this.track(
        'app.route.changed',
        {
          url: navigation.urlAfterRedirects,
        },
        'debug',
      );
    });

    this.oidcSecurityService.isAuthenticated$.subscribe(({ isAuthenticated }) => {
      this.isAuthenticated = isAuthenticated;
      recordRumEvent('auth.state.changed', {
        app: 'rep-flow',
        isAuthenticated,
      });

      this.track(
        'auth.state.changed',
        {
          isAuthenticated,
        },
        isAuthenticated ? 'info' : 'warn',
      );

      if (isAuthenticated) {
        this.flushNow();
      }
    });
  }

  track(type: string, details: Record<string, unknown> = {}, level: ClientDiagnosticLevel = 'info'): void {
    const event: ClientDiagnosticEvent = {
      eventId: this.createEventId(),
      sequence: ++this.sequence,
      type,
      level,
      timestamp: new Date().toISOString(),
      route: this.currentRoute(),
      details: this.sanitizeRecord({
        ...details,
        online: navigator.onLine,
        visibilityState: document.visibilityState,
      }),
    };

    this.queue.push(event);
    if (this.queue.length > this.maxBufferedEvents) {
      this.queue.splice(0, this.queue.length - this.maxBufferedEvents);
    }

    if (level === 'error') {
      this.scheduleFlush(0);
    } else if (level === 'warn') {
      this.scheduleFlush(500);
    } else {
      this.scheduleFlush(1500);
    }
  }

  trackApi(
    type: string,
    details: {
      method: string;
      path: string;
      status?: number;
      requestId?: string;
      message?: string;
      count?: number;
    },
    level: ClientDiagnosticLevel,
  ): void {
    this.track(
      type,
      {
        ...details,
      },
      level,
    );
  }

  flushNow(): void {
    this.scheduleFlush(0);
  }

  toApiPath(url: string): string {
    return url.startsWith(api) ? url.slice(api.length) || '/' : url;
  }

  private readonly handleOnline = () => {
    this.track('app.network.online', {}, 'info');
    this.flushNow();
  };

  private readonly handleOffline = () => {
    this.track('app.network.offline', {}, 'warn');
  };

  private readonly handlePageShow = (event: Event) => {
    const pageEvent = event as PageTransitionEvent;
    this.track(
      'app.pageshow',
      {
        persisted: pageEvent.persisted,
      },
      'info',
    );
    this.flushNow();
  };

  private readonly handlePageHide = (event: Event) => {
    const pageEvent = event as PageTransitionEvent;
    this.track(
      'app.pagehide',
      {
        persisted: pageEvent.persisted,
      },
      'info',
    );
  };

  private readonly handleVisibilityChange = () => {
    this.track(
      'app.visibility.changed',
      {
        visibilityState: document.visibilityState,
      },
      document.visibilityState === 'visible' ? 'info' : 'debug',
    );

    if (document.visibilityState === 'visible') {
      this.flushNow();
    }
  };

  private readonly handleWindowError = (event: Event) => {
    const errorEvent = event as ErrorEvent;
    this.track(
      'app.window.error',
      {
        message: errorEvent.message,
        filename: errorEvent.filename,
        lineno: errorEvent.lineno,
        colno: errorEvent.colno,
      },
      'error',
    );
  };

  private readonly handleUnhandledRejection = (event: Event) => {
    const rejectionEvent = event as PromiseRejectionEvent;
    this.track(
      'app.unhandled_rejection',
      {
        reason: this.sanitizeUnknown(rejectionEvent.reason),
      },
      'error',
    );
  };

  private scheduleFlush(delayMs: number): void {
    if (this.flushInFlight) {
      return;
    }

    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
    }

    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.flush();
    }, delayMs);
  }

  private flush(): void {
    if (this.flushInFlight || !this.queue.length || !navigator.onLine || !this.isAuthenticated) {
      return;
    }

    const batch = this.queue.slice(0, this.maxBatchSize);
    this.flushInFlight = true;

    this.http
      .post(
        `${api}/users/client-diagnostics`,
        {
          app: 'rep-flow',
          sessionId: this.sessionId,
          currentRoute: this.currentRoute(),
          events: batch,
        },
        {
          context: new HttpContext().set(SKIP_CLIENT_DIAGNOSTICS, true),
        },
      )
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.queue.splice(0, batch.length);
          this.flushInFlight = false;
          if (this.queue.length) {
            this.scheduleFlush(150);
          }
        },
        error: (error) => {
          this.flushInFlight = false;
          console.warn('Client diagnostics flush failed', error);

          if (error?.status === 401 || error?.status === 403) {
            this.queue.splice(0, batch.length);
            return;
          }

          this.scheduleFlush(2000);
        },
      });
  }

  private currentRoute(): string {
    return this.router.url || `${window.location.pathname}${window.location.search}`;
  }

  private restoreOrCreateSessionId(): string {
    try {
      const existing = window.sessionStorage?.getItem(SESSION_STORAGE_KEY);
      if (existing) {
        return existing;
      }
      const created = this.createEventId();
      window.sessionStorage?.setItem(SESSION_STORAGE_KEY, created);
      return created;
    } catch {
      return this.createEventId();
    }
  }

  private createEventId(): string {
    const randomUuid = globalThis.crypto?.randomUUID?.();
    if (randomUuid) {
      return randomUuid;
    }

    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private sanitizeRecord(value: Record<string, unknown>): Record<string, unknown> {
    return this.sanitizeUnknown(value) as Record<string, unknown>;
  }

  private sanitizeUnknown(value: unknown, depth = 0): unknown {
    if (depth >= 4) {
      return '[Max depth reached]';
    }

    if (value === null || value === undefined) {
      return value;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length > 400 ? `${trimmed.slice(0, 399)}…` : trimmed;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : String(value);
    }

    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'bigint' || typeof value === 'symbol' || typeof value === 'function') {
      return String(value);
    }

    if (value instanceof Date) {
      return value.toISOString();
    }

    if (Array.isArray(value)) {
      return value.slice(0, 20).map((item) => this.sanitizeUnknown(item, depth + 1));
    }

    if (typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>)
          .slice(0, 30)
          .map(([key, entryValue]) => [key, this.sanitizeUnknown(entryValue, depth + 1)]),
      );
    }

    return String(value);
  }
}
